import type { AeCampaign, AeCampaignListItem, AeCampaignStatus } from '@/types/assessmentEngine';
import { normalizeCampaignListItem } from '@/components/assessment-engine/campaignResponseMode';

/** Locale-aware short date for campaign list columns. */
export function formatCampaignListDate(iso: string | undefined, locale: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** User-facing message from an API client error. */
export function getAssessmentApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: { message?: string } } }).response?.data;
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message.trim();
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

/** Apply lifecycle API result to a list row while keeping aggregate columns from the list endpoint. */
export function mergeCampaignLifecycleIntoListItem(
  row: AeCampaignListItem,
  updated: AeCampaign
): AeCampaignListItem {
  return normalizeCampaignListItem({
    ...row,
    ...updated,
    invite_count: row.invite_count,
    response_count: row.response_count,
    complete_count: row.complete_count,
    administrator_name: row.administrator_name,
    administrator_email: row.administrator_email,
    company_name: row.company_name,
    template_name: row.template_name ?? updated.template?.name,
    template_code: row.template_code ?? updated.template?.code,
  });
}

/** Whether a row should remain visible under the current status filter after a transition. */
export function campaignMatchesStatusFilter(
  status: AeCampaignStatus,
  filter: AeCampaignStatus | 'all'
): boolean {
  return filter === 'all' || filter === status;
}
